import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { Column, ColumnKeys, Module } from '../types';
import { getAllAvailableModulesByObjectType } from '../../../utils/globalExclusions/exclusionsValidationsUtils';
import { getLabelForEnumValue } from '../../../grid/utils/columns';
import { html } from 'lit';
import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { GridViewWebComponent } from '../../../grid/gridView';
import { GridColumnRenderers, GridViewMetadata } from '../../../grid/gridView/types';
import { Filter, getModulesValues, getObjectTypeValues } from '../../exclusionsPanel/types';
import { translateFn } from '../../../utils/translator.helper';
import { ExclusionItemGridDataProvider } from '../dataProvider/ExclusionItemGridDataProvider';

export class ExclusionItemGridConfig {

  private gridColumns: Column[] = [
    Column.OBJECT_TYPE,
    Column.EXCLUDED_ITEMS,
    Column.MODULES,
    Column.REMARKS,
    Column.ACTIONS,
  ];

  private allColumnDetails: Array<ColumnDetails<Column, ColumnKeys>> = [
    {
      column: Column.OBJECT_TYPE,
      key: ColumnKeys.OBJECT_TYPE,
      columnName: translateFn('Object type'),
      type: ColumnType.ENUM,
      values: getObjectTypeValues(),
      removable: false,
    },
    {
      column: Column.EXCLUDED_ITEMS,
      key: ColumnKeys.EXCLUDED_ITEMS,
      columnName: translateFn('Excluded items'),
      type: ColumnType.STRING,
      removable: false,
    },
    {
      column: Column.MODULES,
      key: ColumnKeys.MODULES,
      columnName: translateFn('Modules'),
      type: ColumnType.ENUM,
      values: getModulesValues(),
      removable: false,
    },
    {
      column: Column.REMARKS,
      key: ColumnKeys.REMARKS,
      columnName: translateFn('Remarks'),
      type: ColumnType.STRING,
      removable: false,
    },
    {
      column: Column.ACTIONS,
      key: ColumnKeys.ACTIONS,
      columnName: '',
      type: ColumnType.ENUM,
      removable: false,
      editingDisabled: true,
    },
  ];

  private gridDefaultFilters: Array<Filter> = [{objectType: {in: []}}];

  private predefinedFiltersKeys: Array<ColumnKeys> = [
    ColumnKeys.OBJECT_TYPE,
    ColumnKeys.EXCLUDED_ITEMS,
    ColumnKeys.MODULES,
  ];

  private columnRenderers: GridColumnRenderers = {
    [ColumnKeys.MODULES]: this.modulesRenderer,
    [ColumnKeys.ACTIONS]: this.actionsRenderer.bind(this),
  };

  constructor(private readonly dataProvider: GridBaseDataProvider<Column, ColumnKeys>, private $gridView: GridViewWebComponent<Column, ColumnKeys>) {
  }

  public setGridViewMetadata(grid: GridViewWebComponent<Column, ColumnKeys>) {
    Object.assign(grid, this.getGridViewMetadata());
  }

  public getGridViewMetadata(): GridViewMetadata<Column, ColumnKeys> {
    return {
      viewId: 'exclusion-item-grid',
      filters: this.gridDefaultFilters,
      predefinedFiltersKeys: this.predefinedFiltersKeys,
      columns: this.gridColumns,
      columnRenderers: this.columnRenderers,
      allColumnDetails: this.allColumnDetails,
      dataProvider: this.dataProvider,
      pageSize: 100,
      isCompactViewChecked: false,
      columnConfigs: [],
      pageNumber: 1,
    };
  }

  private modulesRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
    let cellValues = rowData.item[columnDetails.key];
    cellValues.forEach((item, index) => {
      if (item === Module.ALL) {
        cellValues = getAllAvailableModulesByObjectType(rowData.item.objectType);
      }
    });

    const labels = cellValues.map(cellValue => getLabelForEnumValue(columnDetails, cellValue)).join(', ');
    return html`<span class="cell-label">${labels}</span>`;
  }

  private actionsRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
    return html` <span class="cell-label">
    <gz-icon-button
      buttonFill="quiet"
      buttonColor="secondary"
      buttonType="no_hover"
      icon="ds-icon-cancel"
      size="24"
      fontSize="24"
      data-internal-id="${rowData.item.id}"
      @click="${this.onExclusionItemDelete.bind(this)}"
    ></gz-icon-button>
  </span>`;
  }

  public onExclusionItemDelete(ev) {
    const itemId = ev.target.getAttribute('data-internal-id');
    (this.$gridView.dataProvider as ExclusionItemGridDataProvider).deleteItem(itemId);
    this.$gridView.controller?.handleGridItemsLoad();
  }

}
